import { Fetcher } from "query/Fetch";
import Query from "query/Query";
import { Row } from "./Row";
import TableColumn from "./TableColumn";
import { ColumnType } from "query/ColumnType";
import { ColumnPath } from "query/Filter";
import Updater from "./Updater";
import Inserter from "./Inserter";

export class ColumnNotFound extends Error { }

export class PrimaryKey {
    public columns : Array<TableColumn>; // Columns must be unique.

    constructor (columns : Array<TableColumn>) {
        this.columns = columns;
    }

    toString = () => {
        if (this.columns.length !== 1) {
            throw new Error("Cannot convert multi-column primary key to a string.");
        }
        return String(this.columns[0]);
    }

    add(c : TableColumn) {
        this.columns.push(c);
    }

    length() {
        return this.columns.length;
    }

}

export class PrimaryKeyValues {
    public values : Map<TableColumn, any> = new Map();

    get = (name : string) => {
        this.values.forEach((value, key) => {
            if (name===key.name) {
                return value;
            }
        })
        throw new Error(`Table does not contain primary key column: ${name}`);
    }

    set = (key : TableColumn, value : any) => {
        this.values.set(key, value);
    }

    one = () => {
        if (1 !== this.values.size) {
            throw new Error("Primary key has other than one column.");
        }
        return Array.from(this.values.values())[0];
    }

}

// If the first element in the tuple is null, then we are inserting a new row. 
// Otherwise we are updating an existing row.
export type EditedRow = [PrimaryKeyValues|null, Map<TableColumn, any>]; // This is a tuple, not an array.

export default abstract class Table implements TableConstructor {

    public name: string = "Unnamed Table";
    protected _columns: TableColumn = new TableColumn("Root column", ColumnType.RootColumn);
    public primaryKey: PrimaryKey = new PrimaryKey([]);
    private _changeset: Array<EditedRow> = [];

    public static readonly MAX_EXPANDED_ROWS = 10;

    public query = () => {
        return new Query(this);
    }

    /* Return the column hierarchy. */
    get columns() {
        return this._columns;
    }

    set columns(columns: TableColumn) {
        this._columns = columns;
    }

    getColumn = (p: string) => {
        for (let c of this._columns.children) {
            if (p === c.name) {
                return c;
            }
        }
        throw new ColumnNotFound(p);
    }

    public findColumn = (p: string) => {
        for (let c of this._columns.children) {
            if (p === c.name) {
                return c;
            }
        }
        return null;
    }

    public findColumnByPath: (columnPath: string) => ColumnPath | null = (columnPath) => {
        let parts: Array<string> = columnPath.split("/");
        return this.findColumnByPathImpl(this._columns.children, parts);
    }

    public findColumnByPathImpl: (columns: TableColumn[], parts: Array<string>) => ColumnPath | null = (columns, parts) => {
        let [head, ...tail] = parts;
        for (let c of columns) {
            if (head === c.name) {
                if (tail.length === 0) {
                    return [c];
                } else {
                    let newTail: ColumnPath | null = this.findColumnByPathImpl(c.children, tail);
                    if (null === newTail) {
                        return null;
                    } else {
                        return [c, ...newTail];
                    }
                }
            }
        }
        return null;
    }

    /* Get the rows from "from" to "to", according to the parameters in the given query.
    The result is a list of Rows, each Row being a hierarchy based on the query's expanded columns.
    */
    abstract fetch(fetcher: Fetcher, q: Query, from: number, to: number): Promise<Row[]>;

    /* Get the number of rows that would be returned based on that query's filter. */
    abstract fetchCount(fetcher: Fetcher, q: Query): Promise<number>;

    public addColumn = (name: string, type: ColumnType, parent?: TableColumn) => {
        let t: TableColumn = new TableColumn(name, type);
        if (!parent) {
            this._columns.children.push(t);
        } else {
            parent?.children.push(t)
        }
        return t;
    }


    private addToPrimaryKey(column: string) {
        let keyColumn = this.findColumn(column);
        if (null === keyColumn) {
            console.error(`Primary key could not be found: ${column}`);
            return;
        }
        this.primaryKey.add(keyColumn);
    }

    /** Only for use by Updater and Inserter */
    public editRow = (id: PrimaryKeyValues|null) => {
        if (null===id) {
            return this.createChangeSetRow(null);
        }
        let result = this.getChangeSetRow(id);
        if (result) {
            return result;
        } else {
            result = this.createChangeSetRow(id);
            return result;
        }
    }

    private getChangeSetRow = (id: PrimaryKeyValues) => {
        // OPTIMISATION: this won't scale for large changesets.
        // To improve this, the changeset needs to be a hash table, and the primary key
        // needs to be hashed.
        for (let eachRow of this._changeset) {
            let pk: PrimaryKeyValues|null = eachRow[0];
            if (null===pk) {
                continue; // It's an inserted row with no pk yet.
            }
            if (pk===id) {
                return eachRow;
            }
        }
        return null;
    }


    private createChangeSetRow = (id: PrimaryKeyValues|null) => {
        let newRow: EditedRow = [id, new Map()];
        this._changeset.push(newRow);
        return newRow;
    }

    insert() {
        return new Inserter(this);
    }

    update(id: PrimaryKeyValues) {
        return new Updater(this, id);
    }

    commit(fetcher: Fetcher) {
        throw new Error('Method not implemented.');
    }

}


interface TableConstructor {
    addColumn(name: string, type: ColumnType, parent?: TableColumn): void;
}